import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import css from './ShopSettingsForm.module.css';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { CropperModal, Icons, IconSpinner } from '../../../components';
import { useDispatch } from 'react-redux';
import { uploadImage } from '../ShopSettingsPage.duck';
import { showErrorToast, showSuccessToast } from '../../../util/toast';
import placeholder from '../../../assets/profile.png';
import { CropperShape } from '../../../util/enums';

const ProfilePhotoSection = ({ input: { value, onChange } }) => {
  const dispatch = useDispatch();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [tempFileUrl, setTempFileUrl] = useState(null);
  const [isCropperModalOpen, setIsCropperModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = async acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > 10 * 1024 * 1024) {
        showErrorToast('File size exceeds the maximum limit of 10MB.');
        return;
      }

      // Set temporary URL for preview in ProfilePhotoSection
      const tempUrl = URL.createObjectURL(file);
      setTempFileUrl(tempUrl);
      setSelectedFile(file);
      setIsCropperModalOpen(true); // Open CropperModal directly
    }
  };

  const handleSaveCroppedImage = async croppedImage => {
    setUploadInProgress(true);
    setIsCropperModalOpen(false);

    try {
      const response = await dispatch(uploadImage({ file: croppedImage, isProfileImage: true }));
      if (response) {
        onChange(response); // Update with uploaded image URL
        // showSuccessToast('Image uploaded successfully.');
      }
    } catch (error) {
      showErrorToast('Failed to upload image. Please try again.');
    } finally {
      setUploadInProgress(false);
      setTempFileUrl(null);
      setSelectedFile(null);
      if (tempFileUrl) URL.revokeObjectURL(tempFileUrl); // Clean up
    }
  };
  const acceptedTypes = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
  };
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: acceptedTypes,
    maxFiles: 1,
    noClick: true,
    onDrop,
  });

  const getContent = () => {
    const renderMap = {
      uploading: <IconSpinner />,
      tempPreview: tempFileUrl ? (
        <img
          src={tempFileUrl}
          alt="Profile Temp"
          className={classNames(css.preview, css.profilePreview)}
        />
      ) : null,
      hasValue: value ? (
        <img src={value} alt="Profile" className={classNames(css.preview, css.profilePreview)} />
      ) : null,
      placeholder: (
        <div className={css.profilePlaceholder}>
          <img src={placeholder} alt="placeholder" />
        </div>
      ),
    };

    if (uploadInProgress) return renderMap.uploading;
    if (tempFileUrl) return renderMap.tempPreview;
    if (value) return renderMap.hasValue;
    return renderMap.placeholder;
  };

  return (
    <div className={css.profilePhotoSection}>
      <div className={css.profileDropzoneWrapper}>
        <div
          {...getRootProps()}
          className={classNames(css.dropzone, css.profileDropzone, { [css.active]: isDragActive })}
        >
          <input {...getInputProps()} />
          {getContent()}
        </div>
        <span
          className={css.editProfileIcon}
          onClick={e => {
            e.stopPropagation();
            setIsCropperModalOpen(true); // Open CropperModal directly
          }}
        >
          <Icons name="pencil" />
        </span>
      </div>
      {fileRejections.length > 0 && (
        <p className={css.error}>
          <FormattedMessage
            id="ProfilePhotoSection.error"
            defaultMessage="Only JPG, PNG, or non-animated GIF files are allowed."
          />
        </p>
      )}
      <CropperModal
        isOpen={isCropperModalOpen}
        onClose={() => {
          setIsCropperModalOpen(false);
          setTempFileUrl(null);
          setSelectedFile(null);
          if (tempFileUrl) URL.revokeObjectURL(tempFileUrl);
        }}
        onSave={handleSaveCroppedImage}
        acceptedTypes={acceptedTypes}
        imageFile={selectedFile}
        cropShape={CropperShape.ROUND}
        isProfileImage={true}
        aspectRatio={1}
      />
    </div>
  );
};

export default ProfilePhotoSection;
